<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ChevronDown, LogOut, Menu, User, X } from 'lucide-vue-next';
import { UiThemeToggle } from '@dashboard/ui-kit';
import { navSections } from '../modules/nav';
import StepUpDialog from './step-up-dialog.vue';
import { callMe, identity, signOut, siteName } from '../session';

const router = useRouter();
const route = useRoute();
const sections = computed(() => navSections());
const signingOut = ref(false);

const version = __DASHBOARD_VERSION__;

/**
 * The off-canvas nav, below `lg` only.
 *
 * One `<nav>` element rather than a drawer plus a sidebar: the two renderings
 * differ by `translate-x` and nothing else, and a second instance would carry a
 * second copy of the collapsed-section state that the first one disagreed with
 * the moment the window was resized.
 */
const navOpen = ref(false);
const navEl = ref<HTMLElement | undefined>();
const menuButton = ref<HTMLButtonElement | undefined>();

/** Following a link is the point; staying open over the page it opened is not. */
watch(
	() => route.fullPath,
	() => {
		navOpen.value = false;
	},
);

/**
 * No focus trap, deliberately. This same element is the permanent sidebar at
 * `lg` and above, where trapping focus inside the navigation would be a bug,
 * and a trap that switches itself off by viewport is worse than none. What a
 * keyboard user actually needs from an overlay — a way out, and focus that
 * lands somewhere useful — is here without it.
 */
function openNav(): void {
	navOpen.value = true;
	queueMicrotask(() => navEl.value?.querySelector('a')?.focus());
}

function closeNav(): void {
	navOpen.value = false;
	menuButton.value?.focus();
}

function onNavKeydown(event: KeyboardEvent): void {
	if (event.key !== 'Escape') return;
	// The user menu first: it is the one more likely to be on top.
	if (userOpen.value) closeUser();
	else if (navOpen.value) closeNav();
}

/**
 * Collapsed sections, by module id. **Collapsed** is what is stored rather than
 * expanded, so a section that has never been touched is open and a deployment
 * that adds a module does not hide it.
 *
 * Not persisted. The shell is mounted once and wraps the router view, so this
 * survives navigation and resets on reload — a smaller surprise than a nav that
 * comes back collapsed and leaves somebody wondering where their links went.
 */
const collapsed = ref(new Set<string>());

function toggleSection(moduleId: string): void {
	const next = new Set(collapsed.value);
	if (next.has(moduleId)) next.delete(moduleId);
	else next.add(moduleId);
	// Replaced rather than mutated: the same `Set` object is the same object,
	// and nothing downstream of it would recompute.
	collapsed.value = next;
}

/**
 * The user menu.
 *
 * Sign-out used to be a button sitting in the chrome, which put the one
 * irreversible action on the page a click away at every width. Behind a menu it
 * is still two keystrokes and no longer adjacent to everything else.
 *
 * The theme toggle moved in here rather than disappearing on narrow screens.
 * Hiding a control below a breakpoint means somebody on a tablet simply cannot
 * change the theme, and keeping it in the header at wide widths only would put
 * the same control in two places.
 */
const userOpen = ref(false);
const userEl = ref<HTMLElement | undefined>();
const userButton = ref<HTMLButtonElement | undefined>();

function closeUser(): void {
	userOpen.value = false;
}

/**
 * Dismissed by a listener, **not** by a full-screen backdrop element.
 *
 * The backdrop was the obvious thing and it did not work: the header is
 * `fixed z-30`, which makes it a stacking context, so the menu's `z-40` is
 * `z-40` *within the header* and cannot rise above the header's own `z-30` at
 * the root. A root-level backdrop at the same `z-30` that comes later in the
 * document paints on top of the whole header — and every click on Profile or
 * Sign out hit the backdrop instead. `elementFromPoint` over the link returned
 * the backdrop; nothing in a jsdom test could have, because jsdom has no
 * layout and therefore no hit testing.
 */
function onPointerDownOutside(event: MouseEvent): void {
	const target = event.target as Node | null;
	if (target === null) return;
	if (userEl.value?.contains(target) === true) return;
	// The button toggles itself; closing here too would reopen on the same click.
	if (userButton.value?.contains(target) === true) return;
	closeUser();
}

watch(userOpen, (open) => {
	if (open) document.addEventListener('mousedown', onPointerDownOutside);
	else document.removeEventListener('mousedown', onPointerDownOutside);
});

// A shell unmounted with the menu open must not leave the listener attached.
onBeforeUnmount(() => {
	document.removeEventListener('mousedown', onPointerDownOutside);
});

// A menu that survives the navigation it caused is a menu left hanging over
// the page it opened.
watch(
	() => route.fullPath,
	() => {
		userOpen.value = false;
	},
);

async function leave(): Promise<void> {
	signingOut.value = true;
	try {
		await signOut();
		await router.push({ name: 'sign-in' });
	} finally {
		signingOut.value = false;
	}
}
</script>

<template>
	<div class="min-h-screen" @keydown="onNavKeydown">
		<!--
			The header and the nav are `fixed`, which leaves **the body** as the
			scrolling element. That is deliberate: `useOverlay` locks scrolling
			with `document.body.style.overflow`, so moving the scroll into an
			inner container would quietly stop a drawer from holding the page
			still behind it.

			This comment lives inside the root rather than above it: a comment
			before the only element makes the component multi-root, and the root
			node then stops being the `div` these handlers are bound to.
		-->
		<!--
			Fixed rather than sticky. Sticky keeps it in flow, which works
			until any ancestor gains an `overflow`, and then the header scrolls
			away with no error to say why. The sign-out and the theme toggle
			are what somebody reaches for from anywhere, and a long audit log
			should not make them scroll back up for either.

			`h-14` is load-bearing: the nav's top and the content's top padding
			are both measured from it.
		-->
		<header
			class="border-border bg-surface fixed inset-x-0 top-0 z-30 flex h-14 items-center justify-between gap-4 border-b px-5"
		>
			<div class="flex items-center gap-2">
				<button
					v-if="sections.length > 0"
					ref="menuButton"
					type="button"
					class="text-fg hover:bg-border/40 -ml-2 rounded-lg p-2 transition-colors lg:hidden"
					:aria-expanded="navOpen"
					aria-controls="app-nav"
					:aria-label="navOpen ? 'Close navigation' : 'Open navigation'"
					@click="navOpen ? closeNav() : openNav()"
				>
					<component :is="navOpen ? X : Menu" class="size-5" aria-hidden="true" />
				</button>

				<!--
					Hidden below `lg`, where the centred title below takes over.
					Without this the name rendered twice — once here and once in
					the middle of the same header.
				-->
				<!--
					The name alone. `D26` ships this visually unbranded, and
					the mark that used to sit here was a hardcoded `A` — not
					the deployment's initial, not anything a client could
					change. A placeholder that cannot be filled in is worse
					than nothing beside a name that is already the brand.
				-->
				<RouterLink to="/" class="hidden items-center font-semibold lg:flex">
					{{ siteName }}
				</RouterLink>
			</div>
			<!--
				Centred below `lg`, where the hamburger on one side and the user
				button on the other leave it stranded off to the left otherwise.
				`absolute` rather than a flex rule, so it centres on the header
				and not on whatever happens to be beside it.
			-->
			<RouterLink
				to="/"
				class="absolute left-1/2 flex -translate-x-1/2 items-center font-semibold lg:hidden"
			>
				{{ siteName }}
			</RouterLink>

			<div class="relative flex items-center gap-3">
				<button
					ref="userButton"
					type="button"
					class="text-fg hover:bg-border/40 flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors"
					:aria-expanded="userOpen"
					aria-controls="user-menu"
					:aria-label="`Account: ${callMe}`"
					@click="userOpen = !userOpen"
				>
					<span
						class="bg-border/60 text-fg grid size-7 shrink-0 place-items-center rounded-full"
						aria-hidden="true"
					>
						<User class="size-4" />
					</span>
					<!--
						The label goes, not the control. On a narrow screen the
						icon alone still opens the same menu, and the name is
						the first thing inside it.
					-->
					<span class="hidden max-w-48 truncate text-sm lg:inline">{{ callMe }}</span>
					<ChevronDown class="hidden size-4 shrink-0 lg:block" aria-hidden="true" />
				</button>

				<div
					v-if="userOpen"
					id="user-menu"
					ref="userEl"
					class="border-border bg-surface absolute top-full right-0 z-40 mt-1 w-60 overflow-hidden rounded-xl border shadow-lg"
				>
					<div class="border-border border-b px-3 py-2.5">
						<p class="truncate text-sm font-medium">{{ callMe }}</p>
						<!-- Only when it would not simply repeat the line above. -->
						<p
							v-if="identity?.displayName !== undefined"
							class="text-muted truncate text-xs"
						>
							{{ identity.email }}
						</p>
					</div>

					<div class="flex flex-col p-1">
						<RouterLink
							:to="{ name: 'profile' }"
							class="text-fg hover:bg-border/40 flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors"
						>
							<User class="size-4 shrink-0" aria-hidden="true" />
							Profile
						</RouterLink>
					</div>

					<!--
						In the menu rather than the header, at every width.
						Hidden below a breakpoint it would be unreachable on a
						tablet; kept in both places it would be the same control
						twice.
					-->
					<div
						class="border-border flex items-center justify-between gap-2 border-t px-3 py-2"
					>
						<span class="text-muted text-xs">Theme</span>
						<UiThemeToggle />
					</div>

					<!--
						Styled as a menu row rather than as a button: the items
						in here are peers, and a filled button beside a plain
						link reads as the thing you are meant to press.
					-->
					<div class="border-border border-t p-1">
						<button
							type="button"
							class="text-fg hover:bg-border/40 flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors disabled:opacity-60"
							:disabled="signingOut"
							@click="leave"
						>
							<LogOut class="size-4 shrink-0" aria-hidden="true" />
							{{ signingOut ? 'Signing out…' : 'Sign out' }}
						</button>
					</div>
				</div>
			</div>
		</header>

		<!--
			Dismisses the open nav without covering it. Below `lg` only — at
			wider widths the nav is permanent and there is nothing to dismiss.
		-->
		<div
			v-if="navOpen"
			class="fixed inset-0 z-30 bg-black/40 lg:hidden"
			aria-hidden="true"
			@click="closeNav"
		></div>

		<!--
			Pinned to the window, not to the content.

			It used to sit inside the same centered `max-w` wrapper as the page,
			so on a wide screen it drifted inward with the text and left a band
			of empty space to its left. Navigation is chrome: it belongs to the
			window.

			Its own scrollbar, so a deployment with many modules can outgrow the
			viewport without the links becoming unreachable — and so scrolling
			the page never moves it.

			Below `lg` the same element slides off-canvas instead, because a
			fixed 14rem column on a narrow screen covers what it is meant to
			help you reach.
		-->
		<nav
			v-if="sections.length > 0"
			id="app-nav"
			ref="navEl"
			class="border-border bg-surface fixed top-0 bottom-0 left-0 z-40 flex w-64 flex-col border-r transition-transform duration-200 lg:top-14 lg:z-20 lg:w-56 lg:translate-x-0"
			:class="navOpen ? 'translate-x-0' : '-translate-x-full'"
		>
			<!-- The links scroll; the version below them does not. -->
			<div class="flex flex-1 flex-col gap-4 overflow-y-auto px-3 py-6">
				<div v-for="section in sections" :key="section.moduleId">
					<!--
						A button, not a heading with a click handler. This opens
						and closes something, and `aria-expanded` on anything
						that is not a control is a label for nothing.
					-->
					<button
						type="button"
						class="text-muted hover:text-fg flex w-full items-center justify-between gap-2 rounded-lg px-3 py-1 text-xs font-semibold tracking-wider uppercase transition-colors"
						:aria-expanded="!collapsed.has(section.moduleId)"
						:aria-controls="`app-nav-${section.moduleId}`"
						@click="toggleSection(section.moduleId)"
					>
						{{ section.title }}
						<ChevronDown
							class="size-4 shrink-0 transition-transform"
							:class="collapsed.has(section.moduleId) ? '-rotate-90' : ''"
							aria-hidden="true"
						/>
					</button>

					<!--
						`hidden` rather than `v-if`, so the list the button
						names is always in the document for `aria-controls` to
						point at.
					-->
					<ul
						:id="`app-nav-${section.moduleId}`"
						class="mt-0.5 flex flex-col gap-0.5"
						:hidden="collapsed.has(section.moduleId)"
					>
						<li v-for="link in section.links" :key="link.to">
							<!--
								The active item is a filled pill rather than
								coloured text: on a page of several sections the
								eye finds a shape faster than a shade, and a
								colour alone carries it for nobody who cannot
								see the difference.
							-->
							<RouterLink
								:to="link.to"
								class="text-fg hover:bg-border/40 block rounded-lg px-3 py-1.5 text-sm transition-colors"
								active-class="bg-accent-bg! text-accent font-medium"
							>
								{{ link.title }}
							</RouterLink>
						</li>
					</ul>
				</div>
			</div>

			<!--
				The deployed version, compiled in at build, pinned to the foot
				of the nav. An operator asked "what is this client running?" can
				read it off any page — it used to sit under the content, where
				finding it meant scrolling an audit log to the bottom first.
			-->
			<p class="border-border text-muted shrink-0 border-t px-3 py-3 text-xs">
				Version {{ version }}
			</p>
		</nav>

		<!--
			Clear of both fixed elements. The left inset only applies where the
			nav is actually fixed, and only when there is one — a deployment
			with no modules has no sidebar to leave room for.
		-->
		<div class="pt-14" :class="sections.length > 0 ? 'lg:pl-56' : ''">
			<main class="mx-auto w-full max-w-6xl px-5 py-6"><slot /></main>

			<!--
				The version lives at the foot of the nav. Somebody holding no
				roles at all has no nav, and the stamp is the one thing on this
				page an operator may actually have come for — so it falls back
				here rather than disappearing.
			-->
			<p
				v-if="sections.length === 0"
				class="border-border text-muted mt-4 border-t px-5 py-3 text-xs"
			>
				Version {{ version }}
			</p>
		</div>
		<!--
			Mounted once, for the whole app. It registers itself with the api
			client, so a write refused anywhere — core or module — is retried
			after the user types a code, without the view knowing it happened.

			Inside the root, not beside it: a second root makes the shell a
			fragment, and the `keydown` that closes the nav and the account
			menu is bound to the root element.
		-->
		<StepUpDialog />
	</div>
</template>
